import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { normalizeQueryFilter } from '@/lib/normalize-query-filter';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  Supplier,
  SupplierFilters,
  CreateSupplierInput,
  UpdateSupplierInput,
} from '@erp/data-access/interfaces';

const SUPPLIERS_KEY = ['suppliers'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

export function useSuppliers(filters?: SupplierFilters) {
  const ds = useDataService();
  const normalized = normalizeQueryFilter(filters);
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, normalized],
    queryFn: () => ds.suppliers.list(normalized),
  });
}

export function useNextSupplierCode(enabled: boolean) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, 'next-code'],
    queryFn: () => ds.suppliers.peekNextCode(),
    enabled,
    staleTime: 0,
  });
}

export function useSupplier(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, id],
    queryFn: () => ds.suppliers.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<Supplier>(qc, SUPPLIERS_KEY, id),
  });
}

export function useCreateSupplier() {
  const ds = useDataService();
  return useResilientMutation<CreateSupplierInput, Supplier>({
    entity: 'suppliers',
    op: 'create',
    invalidate: [SUPPLIERS_KEY],
    online: (input) => ds.suppliers.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        code: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
        name: input.name,
        type: input.type,
        taxId: input.taxId ?? null,
        address: input.address ?? null,
        city: input.city ?? null,
        phone: input.phone ?? null,
      }) as Supplier,
    applyOptimistic: (qc, newSupplier) => {
      qc.setQueriesData<Supplier[]>({ queryKey: SUPPLIERS_KEY }, (old) =>
        Array.isArray(old) ? [...old, newSupplier].sort((a, b) => a.name.localeCompare(b.name)) : old,
      );
    },
  });
}

export function useUpdateSupplier() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ id: string; input: UpdateSupplierInput }, Supplier>({
    entity: 'suppliers',
    op: 'update',
    invalidate: [SUPPLIERS_KEY],
    online: ({ id, input }) => ds.suppliers.update(id, input),
    // Merge with cached row so untouched fields don't reset to null.
    optimistic: ({ id, input }) => {
      const cached =
        qc.getQueryData<Supplier>([...SUPPLIERS_KEY, id]) ??
        qc
          .getQueriesData<Supplier[]>({ queryKey: SUPPLIERS_KEY })
          .map(([, list]) => Array.isArray(list) ? list.find((s) => s.id === id) : null)
          .find(Boolean) ??
        ({ id } as Supplier);
      return { ...cached, ...input, id } as Supplier;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<Supplier[]>({ queryKey: SUPPLIERS_KEY }, (old) =>
        Array.isArray(old) ? old.map((s) => (s.id === id ? updated : s)) : old,
      );
      qc.setQueryData<Supplier>([...SUPPLIERS_KEY, id], updated);
    },
  });
}

export function useDeleteSupplier() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'suppliers',
    op: 'delete',
    invalidate: [SUPPLIERS_KEY],
    online: (id) => ds.suppliers.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<Supplier[]>({ queryKey: SUPPLIERS_KEY }, (old) =>
        Array.isArray(old) ? old.filter((s) => s.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...SUPPLIERS_KEY, id] });
    },
  });
}
